import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { getCountdown } from "../../helper/Countdown";
// Assuming you are using react-router-dom
import { set } from "react-hook-form";
import useProductVariant from "../../hooks/useProductVariant";
import { getVariantCombinationPrice } from "../../services/productService";
import useJWT from "../../hooks/useJWT";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import useCart from "../../hooks/useCart";
import { useLocation } from "react-router-dom";
import { useAppConfig } from "../../context/AppConfigContext";
import { cleanHtml } from "../../utils/cleanHtml";
import {
  formatCurrencyLocal,
  formatNumberLocal,
} from "../../utils/stringManipulator";
import { useMobile } from "../../hooks/mediaHooks";

const ProductDetail = ({ product, isWholesale = false }) => {
  const isMobile = useMobile();
  const { config } = useAppConfig();
  const { fetchVariant, data, status, error, resetStatus } =
    useProductVariant();
  let sliderRef = useRef(null);
  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const { user, isLoggedIn } = useJWT();
  const navigate = useNavigate();

  const variantPath = params.get("variant") || "";

  const {
    addCart,
    addStatus,
    error: cartError,
    resetStatus: resetCartStatus,
  } = useCart();

  const [timeLeft, setTimeLeft] = useState(getCountdown());

  const [priceRange, setPriceRange] = useState("");
  const [selectedPrice, setSelectedPrice] = useState("");
  const [selectedOriginalPrice, setSelectedOriginalPrice] = useState("");
  const [originalPriceRange, setOriginalPriceRange] = useState("");
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [imageSlider, setImageSlider] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [variantPrices, setVariantPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [selectedDynamicPriceIdx, setSelectedDynamicPriceIdx] = useState(0);

  // increment & decrement
  const baseMinimumQuantity = Math.max(1, Number(product?.minQty ?? 1));
  const selectedDynamicPrice = variantPrices?.[selectedDynamicPriceIdx];
  const effectiveMinimumQuantity =
    selectedDynamicPrice && selectedDynamicPrice.minPurchaseQty > 0
      ? Math.max(baseMinimumQuantity, selectedDynamicPrice.minPurchaseQty)
      : baseMinimumQuantity;

  const [quantity, setQuantity] = useState(effectiveMinimumQuantity);

  useEffect(() => {
    if (quantity < effectiveMinimumQuantity) {
      setQuantity(effectiveMinimumQuantity);
    }
  }, [effectiveMinimumQuantity, quantity]);

  const incrementQuantity = () => setQuantity(quantity + 1);
  const decrementQuantity = () =>
    setQuantity(quantity > effectiveMinimumQuantity ? quantity - 1 : quantity);

  const [mainImage, setMainImage] = useState(productImages[0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getCountdown());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (product && product.id) {
      fetchVariant(product.id);
    }
  }, [product]);

  useEffect(() => {
    if (variantPath) {
      const variant = data.find((v) => v.id === variantPath);
      if (variant) {
        setSelectedVariantId(variant.id);
        const getP = (v) => v?.discountPrice !== null && v?.discountPrice !== undefined && v?.discountPrice > 0 ? v?.discountPrice : v?.price;
        setSelectedPrice(formatCurrencyLocal(getP(variant), config?.currency));
        if (variant.discountPrice !== null && variant.discountPrice !== undefined && variant.discountPrice > 0) {
          setSelectedOriginalPrice(formatCurrencyLocal(variant.price, config?.currency));
        } else {
          setSelectedOriginalPrice("");
        }
      } else {
        setSelectedVariantId(null);
        setSelectedPrice("");
        setSelectedOriginalPrice("");
      }
    }
  }, [variantPath, data, config?.currency]);

  useEffect(() => {
    if (selectedVariantId) {
      const fetchPrices = async () => {
        setLoadingPrices(true);
        try {
          const res = await getVariantCombinationPrice(selectedVariantId);
          if (res?.data) {
            setVariantPrices(res.data);
          } else if (Array.isArray(res)) {
            setVariantPrices(res);
          } else {
            setVariantPrices([]);
          }
          setSelectedDynamicPriceIdx(0);
        } catch (error) {
          console.error("Error fetching variant prices", error);
          setVariantPrices([]);
          setSelectedDynamicPriceIdx(0);
        } finally {
          setLoadingPrices(false);
        }
      };
      fetchPrices();
    } else {
      setVariantPrices([]);
      setSelectedDynamicPriceIdx(0);
    }
  }, [selectedVariantId]);

  useEffect(() => {
    const dataImages = data
      .map((item) => item.image)
      .filter((url) => url && url.trim() !== ""); // skip null/empty

    // Merge them with `productImages`
    setImageSlider([...dataImages, ...productImages]);
  }, [productImages, data]);

  useEffect(() => {
    if (addStatus === "done") {
      resetCartStatus();
      navigate(`/cart`);
    } else if (addStatus === "error") {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: cartError || "Something went wrong while add to cart.",
      });
      resetCartStatus();
    }
  }, [addStatus]);

  useEffect(() => {
    if (data && data.length > 0) {
      const getP = (v) => v?.discountPrice !== null && v?.discountPrice !== undefined && v?.discountPrice > 0 ? v?.discountPrice : v?.price;
      const minPrice = Math.min(...data.map(getP));
      const maxPrice = Math.max(...data.map(getP));
      const hasDiscount = data.some(v => v?.discountPrice !== null && v?.discountPrice !== undefined && v?.discountPrice > 0);

      setPriceRange(
        minPrice === maxPrice
          ? formatCurrencyLocal(minPrice, config?.currency)
          : `${formatCurrencyLocal(minPrice, config?.currency)} - ${formatCurrencyLocal(maxPrice, config?.currency)}`,
      );

      if (hasDiscount) {
        const minOrigPrice = Math.min(...data.map(v => v?.price));
        const maxOrigPrice = Math.max(...data.map(v => v?.price));
        setOriginalPriceRange(
          minOrigPrice === maxOrigPrice
            ? formatCurrencyLocal(minOrigPrice, config?.currency)
            : `${formatCurrencyLocal(minOrigPrice, config?.currency)} - ${formatCurrencyLocal(maxOrigPrice, config?.currency)}`
        );
      } else {
        setOriginalPriceRange("");
      }

      if (data.length === 1) {
        setSelectedVariantId(data[0].id);
        setSelectedPrice(formatCurrencyLocal(getP(data[0]), config?.currency));
        if (data[0].discountPrice !== null && data[0].discountPrice !== undefined && data[0].discountPrice > 0) {
          setSelectedOriginalPrice(formatCurrencyLocal(data[0].price, config?.currency));
        } else {
          setSelectedOriginalPrice("");
        }
      }
    }
  }, [data, config?.currency]);

  useEffect(() => {
    if (product && product.photo !== null) {
      const photos =
        product?.photoData?.length > 0
          ? product?.photoData.map((item) => item.url)
          : [];
      setProductImages(photos);
      setMainImage(
        photos.length > 0 ? photos[0] : "/assets/images/no-image.png",
      );
    } else {
      setProductImages(["/assets/images/no-image.png"]);
      setMainImage("/assets/images/no-image.png");
    }
    const getP = (p) => p?.discountPrice !== null && p?.discountPrice !== undefined && p?.discountPrice > 0 ? p?.discountPrice : p?.price;
    setSelectedPrice(formatCurrencyLocal(getP(product), config?.currency));
    if (product?.discountPrice !== null && product?.discountPrice !== undefined && product?.discountPrice > 0) {
      setSelectedOriginalPrice(formatCurrencyLocal(product?.price, config?.currency));
    } else {
      setSelectedOriginalPrice("");
    }
    setQuantity(Math.max(1, Number(product?.minQty ?? 1)));
  }, [product, config?.currency]);

  const onVariantClick = (variant, index) => {
    console.log(variant.id + " and " + selectedVariantId);

    const getP = (v) => v?.discountPrice !== null && v?.discountPrice !== undefined && v?.discountPrice > 0 ? v?.discountPrice : v?.price;

    setSelectedVariantId(variant.id === selectedVariantId ? null : variant.id);
    setSelectedPrice(
      variant.id === selectedVariantId
        ? ""
        : formatCurrencyLocal(getP(variant), config?.currency),
    );
    setSelectedOriginalPrice(
      variant.id === selectedVariantId
        ? ""
        : (variant?.discountPrice !== null && variant?.discountPrice !== undefined && variant?.discountPrice > 0 ? formatCurrencyLocal(variant?.price, config?.currency) : "")
    );
    if (variant.image && variant.image.trim() !== "") {
      sliderRef.slickGoTo(index);
      setMainImage(variant.image);
    }
  };

  const onAddToCartClicked = () => {
    if (!isLoggedIn) {
      navigate(
        `/login?redirect=${encodeURIComponent(`${product.isGrosir ? "/wholesale" : ""}/product-details/${encodeURIComponent(product?.slug)}?variant=${selectedVariantId}`)}`,
      );
      return;
    }

    const cartItem = {
      productId: product.id,
      variantCombinationId: selectedVariantId,
      applicationUserId: user.id,
      qty: quantity,
      productPriceId: selectedDynamicPrice?.id || "",
    };

    addCart(cartItem);
  };

  const settingsThumbs = {
    dots: false,
    infinite: imageSlider.length > 5,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    focusOnSelect: true,
    variableWidth: true,
  };
  return (
    <section className="product-details py-40">
      <div className="container container-md">
        <div className="row gy-4">
          <div className="col-lg-12">
            <div className="row gy-4">
              <div className="col-xl-6">
                <div className="product-details__left">
                  <div className="product-details__thumb-slider border border-gray-100 rounded-24 overflow-hidden shadow-sm">
                    <div className="w-100 h-100">
                      <div
                        className="product-details__thumb w-100 h-100 flex-center"
                        onClick={() => setIsFullscreen(true)}
                        style={{ cursor: "zoom-in" }}
                      >
                        <img
                          className="w-100 h-100 object-fit-cover"
                          src={mainImage}
                          alt="Main Product"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-24">
                    <div className="product-details__images-slider">
                      <style>{`
                        .product-details__images-slider .slick-track {
                          margin-left: 0 !important;
                        }
                      `}</style>
                      <Slider
                        ref={(slider) => {
                          sliderRef = slider;
                        }}
                        {...settingsThumbs}
                      >
                        {imageSlider.map((image, index) => (
                          <div
                            key={index}
                            style={{ width: "108px", padding: "0 4px" }}
                          >
                            <div
                              className={`border rounded-16 overflow-hidden cursor-pointer ${
                                mainImage === image
                                  ? "border-main-600 border-2"
                                  : "border-gray-100"
                              }`}
                              style={{
                                width: "100px",
                                height: "100px",
                              }}
                              onClick={() => setMainImage(image)}
                            >
                              <img
                                className="w-100 h-100 object-fit-cover thum"
                                src={image}
                                alt={`Thumbnail ${index}`}
                              />
                            </div>
                          </div>
                        ))}
                      </Slider>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xl-6">
                <div
                  className="product-details__content"
                  style={isMobile ? {} : { paddingLeft: 20 }}
                >
                  <h5 className="mb-12 text-2xl">{product?.name}</h5>
                  <div className="flex-align flex-wrap gap-12">
                    {/* <div className="flex-align gap-12 flex-wrap">
                      <div className="flex-align gap-8">
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                        <span className="text-15 fw-medium text-warning-600 d-flex">
                          <i className="ph-fill ph-star" />
                        </span>
                      </div>
                      <span className="text-sm fw-medium text-neutral-600">
                        4.7 Star Rating
                      </span>
                      <span className="text-sm fw-medium text-gray-500">
                        (21,671)
                      </span>
                    </div> */}
                    {/* <span className="text-sm fw-medium text-gray-500">|</span> */}
                    {/* <span className="text-gray-900">
                      {" "}
                      <span className="text-gray-400 text-md">SKU:</span>
                      {product?.code ?? "-"}{" "}
                    </span> */}
                  </div>
                  <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                  <p className="text-gray-700 text-md">
                    {product?.description && product?.description !== ""
                      ? product?.description
                      : "- No description available"}
                  </p>
                  <div className="mt-32 flex-align flex-wrap gap-32">
                    <div className="flex-align gap-8">
                      {/* {product?.discountPrice > 0 &&
                        product?.discountPrice < product?.price && (
                          <h4 className="mb-0">
                            $
                            {Number(product?.discountPrice)
                              ?.toFixed(2)
                              ?.toLocaleString("en-US")}{" "}
                          </h4>
                        )} */}

                      {selectedVariantId && selectedDynamicPrice ? (
                        <h4 className="mb-0">
                          {selectedDynamicPrice.discountPrice > 0 && (
                            <>
                              <span className="text-md text-gray-400 text-decoration-line-through">
                                {formatCurrencyLocal(selectedDynamicPrice.price, config?.currency)}
                              </span>
                              <span className="mx-8 text-md text-gray-400">/</span>
                            </>
                          )}
                          {formatCurrencyLocal(
                            selectedDynamicPrice.discountPrice > 0
                              ? selectedDynamicPrice.discountPrice
                              : selectedDynamicPrice.price,
                            config?.currency,
                          )}
                          {selectedDynamicPrice.uom && (
                            <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                              {" "}/ {selectedDynamicPrice.uom}
                            </span>
                          )}
                        </h4>
                      ) : (
                      <h4 className="mb-0">
                        {selectedOriginalPrice || originalPriceRange ? (
                            <>
                              <span className="text-md text-gray-400 text-decoration-line-through">
                                  {selectedOriginalPrice === "" ? originalPriceRange : selectedOriginalPrice}
                              </span>
                              <span className="mx-8 text-md text-gray-400">/</span>
                            </>
                        ) : null}
                        {selectedPrice === "" ? priceRange : selectedPrice}
                        {product?.uomName && (
                          <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                            {" "}/ {product.uomName}
                          </span>
                        )}
                      </h4>
                      )}

                      {/* {product?.discountPrice > 0 &&
                        product?.discountPrice < product?.price && (
                          <span className="text-md text-gray-500 text-decoration-line-through">
                            $
                            {Number(product?.price)
                              ?.toFixed(2)
                              ?.toLocaleString("en-US")}
                          </span>
                        )} */}
                    </div>
                  </div>
                  <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />

                  {/* <div className="mb-24">
                    <div className="mt-32 flex-align gap-12 mb-16">
                      <span className="w-32 h-32 bg-white flex-center rounded-circle text-main-one-600 box-shadow-xl">
                        <i className="ph-fill ph-lightning" />
                      </span>
                      <h6 className="text-md mb-0 fw-bold text-gray-900">
                        Products are almost sold out
                      </h6>
                    </div>
                    <div
                      className="progress w-100 bg-gray-100 rounded-pill h-8"
                      role="progressbar"
                      aria-label="Basic example"
                      aria-valuenow={32}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div
                        className="progress-bar bg-main-one-600 rounded-pill"
                        style={{ width: "32%" }}
                      />
                    </div>
                    <span className="text-sm text-gray-700 mt-8">
                      Available only:45
                    </span>
                  </div> */}
                  <span
                    className={`text-gray-900 ${Array.isArray(data) && data.length === 1 ? "d-none" : "d-block"} mb-8`}
                  >
                    Variant:
                  </span>
                  <div
                    className={`${Array.isArray(data) && data.length === 1 ? "d-none" : "flex-between"} gap-16 flex-wrap mb-12`}
                  >
                    <div className="d-flex flex-wrap justify-content-start gap-16">
                      {Array.isArray(data) &&
                        data.length > 0 &&
                        data.map((variant, index) => (
                          <button
                            key={index}
                            className={`"product-card__cart ${
                              selectedVariantId === variant.id
                                ? "btn btn-main-one text-white"
                                : "btn bg-main-one-50 text-heading "
                            } ${data.length === 1 ? "flex-fill" : ""} hover-bg-main-one-600 hover-text-white py-11 px-24 rounded-8 d-flex align-items-center gap-8 text-md fw-medium"`}
                            tabIndex={0}
                            onClick={() => onVariantClick(variant, index)}
                            style={{
                              flex:
                                data.length > 1
                                  ? "0 0 calc(50% - 0.75rem)"
                                  : "1 1 100%",
                            }}
                          >
                            {variant?.image !== "" &&
                              variant?.image !== null && (
                                <img
                                  className="w-40 h-40 flex-center"
                                  src={variant.image}
                                  alt=""
                                />
                              )}
                            {variant.name}
                          </button>
                        ))}
                    </div>
                  </div>
                  <span className="mt-12 pt-12" />

                  {selectedVariantId && (data.find((v) => v.id === selectedVariantId)?.isStockAvailable === false || (!loadingPrices && (!variantPrices || variantPrices.length === 0))) && (
                      <div className="mb-16">
                          <span className="fw-bold" style={{ color: "red" }}>
                              Out of Stock
                          </span>
                      </div>
                  )}

                  {/* Dynamic Price List UI */}
                  {selectedVariantId && (
                    <div className="mb-24 mt-16">
                      {loadingPrices ? (
                        <div className="p-12 mb-16 rounded-8 bg-main-one-50 border border-main-one-200">
                          <span className="text-gray-900 fw-bold">
                            Loading prices...
                          </span>
                        </div>
                      ) : variantPrices && variantPrices.length > 0 ? (
                        <>
                          <div className="p-12 mb-16 rounded-8 bg-main-one-50 border border-main-one-200">
                            <span className="text-gray-900 fw-bold">
                              Lowest price:
                            </span>{" "}
                            <span className="fw-bold text-main-one-600">
                              {formatCurrencyLocal(
                                Math.min(...variantPrices.map((v) => v.discountPrice !== null && v.discountPrice !== undefined && v.discountPrice > 0 ? v.discountPrice : v.price)),
                                config?.currency,
                              )}
                            </span>
                          </div>
                          <div className="d-flex flex-column gap-12">
                            {variantPrices.map((vp, idx) => (
                              <label
                                key={idx}
                                className="d-flex align-items-center gap-8 cursor-pointer"
                              >
                                <input
                                  type="radio"
                                  name="dynamic_price"
                                  checked={selectedDynamicPriceIdx === idx}
                                  onChange={() =>
                                    setSelectedDynamicPriceIdx(idx)
                                  }
                                  className="mt-0"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    accentColor: "var(--main-one-600)",
                                  }}
                                />
                                <span className="text-gray-900">
                                  {vp.uom} -{" "}
                                  {vp.discountPrice !== null && vp.discountPrice !== undefined && vp.discountPrice > 0 && (
                                    <>
                                      <span className="text-gray-400 text-decoration-line-through">
                                        {formatCurrencyLocal(vp.price, config?.currency)}
                                      </span>
                                      <span className="mx-4 text-gray-400">/</span>
                                    </>
                                  )}
                                  {formatCurrencyLocal(
                                    vp.discountPrice !== null && vp.discountPrice !== undefined && vp.discountPrice > 0 ? vp.discountPrice : vp.price,
                                    config?.currency,
                                  )}{" "}
                                  / Qty {vp.conversionQty} (
                                  {formatCurrencyLocal(
                                    vp.pricePerBaseUom,
                                    config?.currency,
                                  )}
                                  /{product?.uomName})
                                  {vp.minPurchaseQty > 1 &&
                                    ` Min. Order: ${vp.minPurchaseQty}`}
                                </span>
                              </label>
                            ))}
                          </div>
                        </>
                      ) : null}
                    </div>
                  )}

                  <span className="text-gray-900 d-block mb-8">
                    Quantity:{" "}
                    {(product?.isGrosir || product?.minQty > 1) &&
                      (product?.isGrosir
                        ? `(Wholesale orders must be in minimum of ${formatNumberLocal(product?.minQty)})`
                        : `(Minimum order is ${formatNumberLocal(product?.minQty)})`)}
                  </span>
                  <div className="flex-between gap-16 flex-wrap">
                    <div className="flex-align flex-wrap gap-16">
                      <div className="border border-gray-100 rounded-pill py-9 px-16 flex-align">
                        <button
                          onClick={decrementQuantity}
                          type="button"
                          className="quantity__minus p-4 text-gray-700 hover-text-main-one-600 flex-center"
                        >
                          <i className="ph ph-minus" />
                        </button>
                        <input
                          type="number"
                          className="quantity__input border-0 text-center w-32"
                          value={quantity}
                          readOnly
                        />
                        <button
                          onClick={incrementQuantity}
                          type="button"
                          className="quantity__plus p-4 text-gray-700 hover-text-main-one-600 flex-center"
                        >
                          <i className="ph ph-plus" />
                        </button>
                      </div>
                      <button
                        onClick={onAddToCartClicked}
                        disabled={
                          addStatus === "loading" ||
                          quantity < effectiveMinimumQuantity ||
                          !selectedVariantId ||
                          (selectedVariantId &&
                            data.find((v) => v.id === selectedVariantId)?.isStockAvailable === false) ||
                          (selectedVariantId && !loadingPrices && (!variantPrices || variantPrices.length === 0))
                        }
                        className="btn btn-main-one rounded-pill flex-align d-inline-flex gap-8 px-48"
                      >
                        {" "}
                        <i className="ph ph-shopping-cart" /> Add To Cart
                      </button>
                      {/* qty left info */}
                    </div>
                    {/* <div className="flex-align gap-12">
                      <Link
                        to="#"
                        className="w-52 h-52 bg-gray-50 text-main-one-600 text-xl hover-bg-main-one-600 hover-text-white flex-center rounded-circle"
                      >
                        <i className="ph ph-heart" />
                      </Link>
                      <Link
                        to="#"
                        className="w-52 h-52 bg-gray-50 text-main-one-600 text-xl hover-bg-main-one-600 hover-text-white flex-center rounded-circle"
                      >
                        <i className="ph ph-shuffle" />
                      </Link>
                      <Link
                        to="#"
                        className="w-52 h-52 bg-gray-50 text-main-one-600 text-xl hover-bg-main-one-600 hover-text-white flex-center rounded-circle"
                      >
                        <i className="ph ph-share-network" />
                      </Link>
                    </div> */}
                  </div>
                  <span className="mt-32 pt-32 text-gray-700 border-top border-gray-100 d-block" />
                </div>
              </div>
            </div>
          </div>
          {/* <div className="col-lg-3">
                        <div className="product-details__sidebar border border-gray-100 rounded-16 overflow-hidden">
                            <div className="p-24">
                                <div className="flex-between bg-main-600 rounded-pill p-8">
                                    <div className="flex-align gap-8">
                                        <span className="w-44 h-44 bg-white rounded-circle flex-center text-2xl">
                                            <i className="ph ph-storefront" />
                                        </span>
                                        <span className="text-white">by Marketpro</span>
                                    </div>
                                    <Link
                                        to="/shop"
                                        className="btn btn-white rounded-pill text-uppercase"
                                    >
                                        View Store
                                    </Link>
                                </div>
                            </div>
                            <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-truck" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Fast Delivery</h6>
                                    <p className="text-gray-700">
                                        Lightning-fast shipping, guaranteed.
                                    </p>
                                </div>
                            </div>
                            <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-arrow-u-up-left" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Free 90-day returns</h6>
                                    <p className="text-gray-700">Shop risk-free with easy returns.</p>
                                </div>
                            </div>
                            <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-check-circle" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">
                                        Pickup available at Shop location
                                    </h6>
                                    <p className="text-gray-700">Usually ready in 24 hours</p>
                                </div>
                            </div>
                            <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-credit-card" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Payment</h6>
                                    <p className="text-gray-700">
                                        Payment upon receipt of goods, Payment by card in the
                                        department, Google Pay, Online card.
                                    </p>
                                </div>
                            </div>
                            <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-check-circle" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Warranty</h6>
                                    <p className="text-gray-700">
                                        The Consumer Protection Act does not provide for the return of
                                        this product of proper quality.
                                    </p>
                                </div>
                            </div>
                            <div className="p-24 bg-color-one d-flex align-items-start gap-24 border-bottom border-gray-100">
                                <span className="w-44 h-44 bg-white text-main-600 rounded-circle flex-center text-2xl flex-shrink-0">
                                    <i className="ph-fill ph-package" />
                                </span>
                                <div className="">
                                    <h6 className="text-sm mb-8">Packaging</h6>
                                    <p className="text-gray-700">
                                        Research &amp; development value proposition graphical user
                                        interface investor.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div> */}
        </div>
        <div className="pt-80">
          <div className="product-dContent border rounded-24">
            <div className="product-dContent__header border-bottom border-gray-100 flex-between flex-wrap gap-16">
              <ul
                className="nav common-tab nav-pills mb-3"
                id="pills-tab"
                role="tablist"
              >
                <li className="nav-item" role="presentation">
                  <button
                    className="nav-link active"
                    id="pills-description-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-description"
                    type="button"
                    role="tab"
                    aria-controls="pills-description"
                    aria-selected="true"
                  >
                    Description
                  </button>
                </li>
                {/* <li className="nav-item" role="presentation">
                  <button
                    className="nav-link"
                    id="pills-reviews-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#pills-reviews"
                    type="button"
                    role="tab"
                    aria-controls="pills-reviews"
                    aria-selected="false"
                  >
                    Reviews
                  </button>
                </li> */}
              </ul>
            </div>
            <div className="product-dContent__box" style={{ padding: "20px" }}>
              <div className="tab-content" id="pills-tabContent">
                <div
                  className="tab-pane fade show active"
                  id="pills-description"
                  role="tabpanel"
                  aria-labelledby="pills-description-tab"
                  tabIndex={0}
                >
                  <div className="mb-0">
                    <div
                      className="text-md"
                      dangerouslySetInnerHTML={{
                        __html: cleanHtml(product?.ecommerceDescription || ""),
                      }}
                    ></div>
                    {/* <h6 className="mb-24">Product Description</h6>
                    <p>
                      Wherever celebrations and good times happen, the LAY'S
                      brand will be there just as it has been for more than 75
                      years. With flavors almost as rich as our history, we have
                      a chip or crisp flavor guaranteed to bring a smile on your
                      face.{" "}
                    </p>
                    <p>
                      Morbi ut sapien vitae odio accumsan gravida. Morbi vitae
                      erat auctor, eleifend nunc a, lobortis neque. Praesent
                      aliquam dignissim viverra. Maecenas lacus odio, feugiat eu
                      nunc sit amet, maximus sagittis dolor. Vivamus nisi
                      sapien, elementum sit amet eros sit amet, ultricies cursus
                      ipsum. Sed consequat luctus ligula. Curabitur laoreet
                      rhoncus blandit. Aenean vel diam ut arcu pharetra
                      dignissim ut sed leo. Vivamus faucibus, ipsum in
                      vestibulum vulputate, lorem orci convallis quam, sit amet
                      consequat nulla felis pharetra lacus. Duis semper erat
                      mauris, sed egestas purus commodo vel.
                    </p>
                    <ul className="list-inside mt-32 ms-16">
                      <li className="text-gray-400 mb-4">
                        8.0 oz. bag of LAY'S Classic Potato Chips
                      </li>
                      <li className="text-gray-400 mb-4">
                        Tasty LAY's potato chips are a great snack
                      </li>
                      <li className="text-gray-400 mb-4">
                        Includes three ingredients: potatoes, oil, and salt
                      </li>
                      <li className="text-gray-400 mb-4">
                        Gluten free product
                      </li>
                    </ul>
                    <ul className="mt-32">
                      <li className="text-gray-400 mb-4">Made in USA</li>
                      <li className="text-gray-400 mb-4">Ready To Eat.</li>
                    </ul> */}
                  </div>
                  {/* <div className="mb-40">
                    <h6 className="mb-24">Product Specifications</h6>
                    <ul className="mt-32">
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          Product Type:
                          <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                            {" "}
                            Chips &amp; Dips
                          </span>
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          Product Name:
                          <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                            {" "}
                            Potato Chips Classic{" "}
                          </span>
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          Brand:
                          <span className="text-gray-500" style={{ fontSize: "0.75em" }}> Lay's</span>
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          FSA Eligible:
                          <span className="text-gray-500" style={{ fontSize: "0.75em" }}> No</span>
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          Size/Count:
                          <span className="text-gray-500" style={{ fontSize: "0.75em" }}> 8.0oz</span>
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          Item Code:
                          <span className="text-gray-500" style={{ fontSize: "0.75em" }}> 331539</span>
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          Ingredients:
                          <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                            {" "}
                            Potatoes, Vegetable Oil, and Salt.
                          </span>
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="mb-40">
                    <h6 className="mb-24">Nutrition Facts</h6>
                    <ul className="mt-32">
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          {" "}
                          Total Fat 10g 13%
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          {" "}
                          Saturated Fat 1.5g 7%
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          {" "}
                          Cholesterol 0mg 0%
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          {" "}
                          Sodium 170mg 7%
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-heading fw-medium">
                          {" "}
                          Potassium 350mg 6%
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="mb-0">
                    <h6 className="mb-24">More Details</h6>
                    <ul className="mt-32">
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                          {" "}
                          Lunarlon midsole delivers ultra-plush responsiveness
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                          {" "}
                          Encapsulated Air-Sole heel unit for lightweight
                          cushioning
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                          {" "}
                          Colour Shown: Ale Brown/Black/Goldtone/Ale Brown
                        </span>
                      </li>
                      <li className="text-gray-400 mb-14 flex-align gap-14">
                        <span className="w-20 h-20 bg-main-one-50 text-main-one-600 text-xs flex-center rounded-circle">
                          <i className="ph ph-check" />
                        </span>
                        <span className="text-gray-500" style={{ fontSize: "0.75em" }}>
                          {" "}
                          Style: 805899-202
                        </span>
                      </li>
                    </ul>
                  </div> */}
                </div>
                <div
                  className="tab-pane fade"
                  id="pills-reviews"
                  role="tabpanel"
                  aria-labelledby="pills-reviews-tab"
                  tabIndex={0}
                >
                  <div className="row g-4">
                    <div className="col-lg-6">
                      <h6 className="mb-24">Product Description</h6>
                      <div className="d-flex align-items-start gap-24 pb-44 border-bottom border-gray-100 mb-44">
                        <img
                          src="assets/images/thumbs/comment-img1.png"
                          alt=""
                          className="w-52 h-52 object-fit-cover rounded-circle flex-shrink-0"
                        />
                        <div className="flex-grow-1">
                          <div className="flex-between align-items-start gap-8 ">
                            <div className="">
                              <h6 className="mb-12 text-md">Nicolas cage</h6>
                              <div className="flex-align gap-8">
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                            </div>
                            <span className="text-gray-800 text-xs">
                              3 Days ago
                            </span>
                          </div>
                          <h6 className="mb-14 text-md mt-24">
                            Greate Product
                          </h6>
                          <p className="text-gray-700">
                            There are many variations of passages of Lorem Ipsum
                            available, but the majority have suffered alteration
                            in some form, by injected humour
                          </p>
                          <div className="flex-align gap-20 mt-44">
                            <button className="flex-align gap-12 text-gray-700 hover-text-main-one-600">
                              <i className="ph-bold ph-thumbs-up" />
                              Like
                            </button>
                            <Link
                              to="#comment-form"
                              className="flex-align gap-12 text-gray-700 hover-text-main-600"
                            >
                              <i className="ph-bold ph-arrow-bend-up-left" />
                              Replay
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-start gap-24">
                        <img
                          src="assets/images/thumbs/comment-img1.png"
                          alt=""
                          className="w-52 h-52 object-fit-cover rounded-circle flex-shrink-0"
                        />
                        <div className="flex-grow-1">
                          <div className="flex-between align-items-start gap-8 ">
                            <div className="">
                              <h6 className="mb-12 text-md">Nicolas cage</h6>
                              <div className="flex-align gap-8">
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-15 fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                            </div>
                            <span className="text-gray-800 text-xs">
                              3 Days ago
                            </span>
                          </div>
                          <h6 className="mb-14 text-md mt-24">
                            Greate Product
                          </h6>
                          <p className="text-gray-700">
                            There are many variations of passages of Lorem Ipsum
                            available, but the majority have suffered alteration
                            in some form, by injected humour
                          </p>
                          <div className="flex-align gap-20 mt-44">
                            <button className="flex-align gap-12 text-gray-700 hover-text-main-600">
                              <i className="ph-bold ph-thumbs-up" />
                              Like
                            </button>
                            <Link
                              to="#comment-form"
                              className="flex-align gap-12 text-gray-700 hover-text-main-600"
                            >
                              <i className="ph-bold ph-arrow-bend-up-left" />
                              Replay
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="mt-56">
                        <div className="">
                          <h6 className="mb-24">Write a Review</h6>
                          <span className="text-heading mb-8">
                            What is it like to Product?
                          </span>
                          <div className="flex-align gap-8">
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                            <span className="text-15 fw-medium text-warning-600 d-flex">
                              <i className="ph-fill ph-star" />
                            </span>
                          </div>
                        </div>
                        <div className="mt-32">
                          <form action="#">
                            <div className="mb-32">
                              <label
                                htmlFor="title"
                                className="text-neutral-600 mb-8"
                              >
                                Review Title
                              </label>
                              <input
                                type="text"
                                className="common-input rounded-8"
                                id="title"
                                placeholder="Great Products"
                              />
                            </div>
                            <div className="mb-32">
                              <label
                                htmlFor="desc"
                                className="text-neutral-600 mb-8"
                              >
                                Review Content
                              </label>
                              <textarea
                                className="common-input rounded-8"
                                id="desc"
                                defaultValue={
                                  "It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English."
                                }
                              />
                            </div>
                            <button
                              type="submit"
                              className="btn btn-main-one rounded-pill mt-48"
                            >
                              Submit Review
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="ms-xxl-5">
                        <h6 className="mb-24">Customers Feedback</h6>
                        <div className="d-flex flex-wrap gap-44">
                          <div className="border border-gray-100 rounded-8 px-40 py-52 flex-center flex-column flex-shrink-0 text-center">
                            <h2 className="mb-6 text-main-one-600">4.8</h2>
                            <div className="flex-center gap-8">
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                              <span className="text-15 fw-medium text-warning-600 d-flex">
                                <i className="ph-fill ph-star" />
                              </span>
                            </div>
                            <span className="mt-16 text-gray-500">
                              Average Product Rating
                            </span>
                          </div>
                          <div className="border border-gray-100 rounded-8 px-24 py-40 flex-grow-1">
                            <div className="flex-align gap-8 mb-20">
                              <span className="text-gray-900 flex-shrink-0">
                                5
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={70}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-one-600 rounded-pill"
                                  style={{ width: "70%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                124
                              </span>
                            </div>
                            <div className="flex-align gap-8 mb-20">
                              <span className="text-gray-900 flex-shrink-0">
                                4
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={50}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-one-600 rounded-pill"
                                  style={{ width: "50%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                52
                              </span>
                            </div>
                            <div className="flex-align gap-8 mb-20">
                              <span className="text-gray-900 flex-shrink-0">
                                3
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={35}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-one-600 rounded-pill"
                                  style={{ width: "35%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                12
                              </span>
                            </div>
                            <div className="flex-align gap-8 mb-20">
                              <span className="text-gray-900 flex-shrink-0">
                                2
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={20}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-one-600 rounded-pill"
                                  style={{ width: "20%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                5
                              </span>
                            </div>
                            <div className="flex-align gap-8 mb-0">
                              <span className="text-gray-900 flex-shrink-0">
                                1
                              </span>
                              <div
                                className="progress w-100 bg-gray-100 rounded-pill h-8"
                                role="progressbar"
                                aria-label="Basic example"
                                aria-valuenow={5}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              >
                                <div
                                  className="progress-bar bg-main-one-600 rounded-pill"
                                  style={{ width: "5%" }}
                                />
                              </div>
                              <div className="flex-align gap-4">
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                                <span className="text-xs fw-medium text-warning-600 d-flex">
                                  <i className="ph-fill ph-star" />
                                </span>
                              </div>
                              <span className="text-gray-900 flex-shrink-0">
                                2
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isFullscreen && (
        <div
          className="fullscreen-overlay"
          onClick={() => setIsFullscreen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <img
            src={mainImage}
            alt="Fullscreen"
            style={{
              maxHeight: "90vh",
              maxWidth: "90vw",
              objectFit: "contain",
            }}
          />
          <button
            onClick={() => setIsFullscreen(false)}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "30px",
              cursor: "pointer",
            }}
          >
            &times;
          </button>
        </div>
      )}
    </section>
  );
};

export default ProductDetail;
